import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import ReactECharts from 'echarts-for-react';
import api from '../api/axios';
import 'react-datepicker/dist/react-datepicker.css';
import BLUE_SHADES, { getBlueShade } from '../constants/chartColors';
import * as moment from "moment-timezone";
import { useTranslation } from 'react-i18next';

import MetricCard from '../components/MetricCard';


function AssociatesTable({ data }) {
  const { t } = useTranslation();
  if (!data || data.length === 0) return null;

  return (
    <div className="bg-gray-800 rounded-2xl shadow w-full max-w-full">
      <h2 className="text-lg sm:text-xl p-4 sm:p-5 font-semibold border-b border-gray-700">
        {t('associatesBreakdown')}
      </h2>

      <div className="max-h-[480px] overflow-auto overflow-x-auto">
        <table className="min-w-max w-full text-sm">
          <thead className="bg-gray-700 sticky top-0 z-10">
            <tr>
              <th className="px-5 py-3 text-left whitespace-nowrap">{t('associate')}</th>
              <th className="px-5 py-3 text-right whitespace-nowrap">{t('daysWorked')}</th>
              <th className="px-5 py-3 text-right whitespace-nowrap">{t('totalHours')}</th>
              <th className="px-5 py-3 text-right whitespace-nowrap">{t('avgHoursDay')}</th>
              <th className="px-5 py-3 text-right whitespace-nowrap">{t('expectedHours')}</th>
              <th className="px-5 py-3 text-right whitespace-nowrap">{t('dueHours')}</th>
              <th className="px-5 py-3 text-right whitespace-nowrap">{t('overtimeH')}</th>
            </tr>
          </thead>

          <tbody>
            {data.map((row, i) => (
              <tr
                key={row.userId}
                className={`border-b border-gray-700 ${
                  i % 2 === 0 ? "bg-gray-900" : "bg-gray-800/50"
                }`}
              >
                <td className="px-5 py-3 font-medium whitespace-nowrap">{row.user}</td>
                <td className="px-5 py-3 text-right">{row.daysWorked}</td>
                <td className="px-5 py-3 text-right">{row.totalHours}</td>
                <td className="px-5 py-3 text-right">{Number(row.averageHours).toFixed(2)}</td>
                <td className="px-5 py-3 text-right">{row.expectedWorkingHours}</td>
                <td className="px-5 py-3 text-right text-orange-400">{row.totalDueHours}</td>
                <td className="px-5 py-3 text-right text-yellow-400">{row.totalOverTimeHours}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AssociateMetricsPage() {
  const today = new Date();
  const defaultEnd = today;
  const defaultStart = new Date(today);
  defaultStart.setDate(today.getDate() - 30);
  const { t } = useTranslation();

  const [dateRange, setDateRange] = useState([defaultStart, defaultEnd]);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [associateData, setAssociateData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [metrics, setMetrics] = useState({
    totalHours: 0,
    totalOvertime: 0,
    totalDue: 0,
    avgHoursPerAssociate: '—',
    associatesCount: 0,
  });

  const maxRangeDays = 90;

  useEffect(() => {
    if (dateRange[0]) setStartDate(dateRange[0]);
    if (dateRange[1]) setEndDate(dateRange[1]);
  }, [dateRange]);

  const fetchData = async () => {
    if (!startDate || !endDate) return;
    if (startDate > endDate) {
      setError(t('errorStartAfterEnd'));
      return;
    }

    const diffDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays > maxRangeDays) {
      setError(t('errorMaxRangeDays', { days: maxRangeDays }));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const companyId = localStorage.getItem("companyId");
      if (!companyId) throw new Error('Company ID not found');

      const start = moment.tz(startDate, "Asia/Tashkent").utc().toISOString();
      const end = moment.tz(endDate, "Asia/Tashkent").endOf("D").utc().toISOString();

      const res = await api.get(`/metrics/${companyId}/associates?startDate=${start}&endDate=${end}`);

      const raw = res.data.data || [];

      const aggregated = raw.reduce((acc, entry) => {
        const key = entry.userId;
        if (!acc[key]) {
          acc[key] = {
            userId: key,
            user: entry.userTitle || entry.userId,
            totalHours: 0,
            averageHours: 0,
            avgLateMinutes: 0,
            avgEarlyMinutes: 0,
            totalOverTimeHours: 0,
            totalDueHours: 0,
            expectedWorkingHours: 0,
            daysWorked: 0,
            daysWithData: new Set(),
          };
        }

        acc[key].totalHours += entry.totalHours || 0;
        acc[key].avgLateMinutes += Number(entry.avgLateMinutes).toFixed(2) || 0;
        acc[key].avgEarlyMinutes += Number(entry.avgEarlyMinutes).toFixed(2) || 0;
        acc[key].averageHours += entry.averageHours || 0;
        acc[key].totalOverTimeHours += entry.totalOverTimeHours || 0;
        acc[key].totalDueHours += entry.totalDueHours || 0;
        acc[key].expectedWorkingHours += entry.expectedWorkingHours || 0;
        acc[key].daysWithData.add(entry.DateLocal);
        acc[key].daysWorked = acc[key].daysWithData.size;

        return acc;
      }, {});

      const aggregatedArray = Object.values(aggregated).map(item => ({
        ...item
      }));

      setAssociateData(aggregatedArray);

      if (aggregatedArray.length > 0) {
        const totalHours = aggregatedArray.reduce((s, d) => s + d.totalHours, 0);
        const averageHours = aggregatedArray.reduce((s, d) => s + d.averageHours, 0);
        const totalOvertime = aggregatedArray.reduce((s, d) => s + d.totalOverTimeHours, 0);
        const totalDue = aggregatedArray.reduce((s, d) => s + d.totalDueHours, 0);
        const avgHours = (averageHours / aggregatedArray.length).toFixed(1);

        setMetrics({
          totalHours,
          totalOvertime,
          totalDue,
          avgHoursPerAssociate: Math.round(avgHours),
          associatesCount: aggregatedArray.length,
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || t('errorLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const names = associateData.map(item => item.user);

  const VISIBLE_BAR_COUNT = 8;
  const BAR_HEIGHT = 42;
  const HEADER_HEIGHT = 50;
  const CHART_HEIGHT = HEADER_HEIGHT + VISIBLE_BAR_COUNT * BAR_HEIGHT;
  const MIN_CHART_WIDTH = 0;

  const getBaseDataSorted = (sortKey) => {
    let data = associateData;
    if (typeof sortKey === "string") {
        data = associateData.sort((a, b) => a[sortKey] - b[sortKey]);
    } else {
        data = associateData.sort((a, b) => (a[sortKey[0]] + a[sortKey[1]]) - (b[sortKey[0]] + b[sortKey[1]]));
    }
    
    associateData.reverse();

    const names = data.map(item => item.user);
    const hours = data.map(item => item.totalHours);
    const overtime = data.map(item => item.totalOverTimeHours);
    const due = data.map(item => item.totalDueHours);
    const avgDaily = data.map(item => Number(item.averageHours).toFixed(2));
    const avgLateMinutes = data.map(item => item.avgLateMinutes);
    const avgEarlyMinutes = data.map(item => item.avgEarlyMinutes);

    return {
        baseOptions: {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            grid: {
                left: '1%',
                right: '14%',
                top: '2%',
                bottom: '2%',
                containLabel: true,
            },
            yAxis: {
                type: 'category',
                data: names,
                inverse: true,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: '#ddd',
                    fontSize: 13,
                    margin: 12,
                    interval: 0,
                },
            },
            xAxis: {
                type: 'value',
                axisLine: { lineStyle: { color: '#666' } },
                axisTick: { show: false },
                splitLine: { lineStyle: { color: '#444', type: 'dashed' } },
                axisLabel: { color: '#aaa', formatter: `{value} ${t('hours')}` },
            },
        },
        names,
        hours,
        overtime,
        due,
        avgLateMinutes,
        avgEarlyMinutes,
        avgDaily
    }
  }

  const totalsBase = getBaseDataSorted("totalHours");
  const overTimeBase = getBaseDataSorted(["totalDueHours", "totalOverTimeHours"]);
  const avgBase  = getBaseDataSorted("averageHours");
  const avgLatencyBase = getBaseDataSorted("avgLateMinutes");

  const totalHoursOption = {
    ...totalsBase.baseOptions,
    series: [{
      type: 'bar',
      data: totalsBase.hours,
      barWidth: 28,
      itemStyle: { color: BLUE_SHADES.primary, borderRadius: [0, 6, 6, 0] },
      label: {
        show: true,
        position: 'right',
        distance: 8,
        formatter: `{c} ${t('hours')}`,
        color: '#eee',
        fontSize: 12,
      },
    }],
  };

  const overtimeDueOption = {
    ...overTimeBase.baseOptions,
    legend: {
      data: [t('overtime'), t('dueMissing')],
      textStyle: { color: '#ddd' },
      top: 0,
      right: 10,
    },
    series: [
      {
        name: t('overtime'),
        type: 'bar',
        stack: 'total',
        data: overTimeBase.overtime,
        barWidth: 28,
        itemStyle: { color: getBlueShade(1), borderRadius: [0, 6, 6, 0] },
      },
      {
        name: t('dueMissing'),
        type: 'bar',
        stack: 'total',
        data: overTimeBase.due,
        barWidth: 28,
        itemStyle: { color: BLUE_SHADES.light2, borderRadius: [0, 6, 6, 0] },
      },
    ],
  };

  const avgDailyOption = {
    ...avgBase.baseOptions,
    series: [{
      type: 'bar',
      data: avgBase.avgDaily,
      barWidth: 28,
      itemStyle: { color: BLUE_SHADES.dark1, borderRadius: [0, 6, 6, 0] },
      label: {
        show: true,
        position: 'right',
        distance: 8,
        formatter: `{c} ${t('hoursPerDay')}`,
        color: '#eee',
        fontSize: 12,
      },
    }],
  };

  const latencyOption = {
    ...avgLatencyBase.baseOptions,
    legend: {
      data: [t('lateMinutesAvg'), t('earlyArrivalMinutesAvg')],
      textStyle: { color: '#ddd' },
      top: 0,
      right: 10,
    },
    series: [
      {
        name: t('lateMinutesAvg'),
        type: 'bar',
        stack: 'total',
        data: avgLatencyBase.avgLateMinutes,
        barWidth: 28,
        itemStyle: { color: getBlueShade(1), borderRadius: [0, 6, 6, 0] },
      },
      {
        name: t('earlyArrivalMinutesAvg'),
        type: 'bar',
        stack: 'total',
        data: avgLatencyBase.avgEarlyMinutes,
        barWidth: 28,
        itemStyle: { color: BLUE_SHADES.light2, borderRadius: [0, 6, 6, 0] },
      },
    ],
  };

  return (
    <div className="p-4 sm:p-6 text-white space-y-6 sm:space-y-8 min-h-screen bg-gray-900">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 sm:gap-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-blue-400">{t('associateMetrics')}</h1>

        <div className="flex flex-wrap items-end gap-4" style={{ zIndex: 9999 }}>
          <div>
            <label className="block text-sm text-gray-400 mb-1">{t('dateRange')}</label>
            <DatePicker
              selectsRange={true}
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={(update) => setDateRange(update)}
              minDate={new Date(new Date().getTime() - maxRangeDays * 24 * 60 * 60 * 1000)}
              maxDate={new Date()}
              dateFormat="yyyy-MM-dd"
              className="bg-gray-800 border border-gray-700 rounded px-4 py-2.5 w-full sm:w-full sm:w-64 text-white cursor-pointer"
              placeholderText={t('dateRange')}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>

          <button
            onClick={fetchData}
            disabled={loading}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all duration-200 shadow-lg ${
              loading ? 'bg-gray-700 cursor-not-allowed text-gray-400' : 'bg-sky-600 hover:bg-sky-500 text-white shadow-sky-900/20 active:scale-95'
            }`}
          >
            {loading ? t('loading') : t('apply')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/60 border border-red-700 text-red-100 px-5 py-3 rounded-xl">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-5">
          <MetricCard title={t('totalHours')} value={metrics.totalHours} subtitle={t('allAssociates')} />
          <MetricCard title={t('avgHoursPerAssociate')} value={metrics.avgHoursPerAssociate} subtitle={t('periodTotal')} />
          <MetricCard title={t('overtimeHours')} value={metrics.totalOvertime} subtitle={t('total')} color="text-blue-400" />
          <MetricCard title={t('dueMissingHours')} value={metrics.totalDue} subtitle={t('total')} color="text-blue-400" />
          <MetricCard title={t('associates')} value={metrics.associatesCount} subtitle={t('withData')} color="text-blue-400" />
        </div>
      </div>

      <div className="space-y-8 pb-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow flex flex-col">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('totalHours')}</h2>
            <div className="flex-1 overflow-hidden">
              <div className="overflow-x-auto" style={{ maxHeight: `${CHART_HEIGHT}px` }}>
                <ReactECharts
                  option={totalHoursOption}
                  style={{
  height: `${HEADER_HEIGHT + names.length * BAR_HEIGHT}px`,
  width: "100%",
}}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow flex flex-col">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('overtimeHours')} & {t('dueMissingHours')}</h2>
            <div className="flex-1 overflow-hidden">
              <div className="overflow-x-auto" style={{ maxHeight: `${CHART_HEIGHT}px` }}>
                <ReactECharts
                  option={overtimeDueOption}
                  style={{
  height: `${HEADER_HEIGHT + names.length * BAR_HEIGHT}px`,
  width: "100%",
}}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadowflex flex-col">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('avgDailyHours')}</h2>
            <div className="flex-1 overflow-hidden">
              <div className="overflow-x-auto" style={{ maxHeight: `${CHART_HEIGHT}px` }}>
                <ReactECharts
                  option={avgDailyOption}
                  style={{
  height: `${HEADER_HEIGHT + names.length * BAR_HEIGHT}px`,
  width: "100%",
}}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow flex flex-col">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('avgLatencyEarly')}</h2>
            <div className="flex-1 overflow-hidden">
              <div className="overflow-x-auto" style={{ maxHeight: `${CHART_HEIGHT}px` }}>
                <ReactECharts
                  option={latencyOption}
                  style={{
  height: `${HEADER_HEIGHT + names.length * BAR_HEIGHT}px`,
  width: "100%",
}}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </div>
            </div>
          </div>
        </div>

        <AssociatesTable data={associateData} />
      </div>
    </div>
  );
}